import {
  Card,
  Text,
  Button,
  Flex,
  Divider,
  Loader,
  useMantineTheme,
  ScrollArea,
  List,
  ThemeIcon,
} from "@mantine/core";
import { useParams } from "react-router-dom";
import { useBrandingContext } from "src/api/context/BrandingContext";
import { PATH_NAMES } from "src/utils/constants";
import {
  LocationOfferingManyQuery,
  LocationOfferingByPromoCodeQuery,
  useGetOrganizationBrandingQuery,
  useLocationOfferingManyQuery,
  useLocationOfferingByPromoCodeLazyQuery,
} from "src/generated/graphql";
import { closeAllModals, openConfirmModal } from "@mantine/modals";
import { useEffect, useState } from "react";
import { AiFillCheckCircle } from "react-icons/ai";
import TextInput from "src/components/TextInput/TextInput";

function MemberShips() {
  const theme = useMantineTheme();
  const { setIsFlatPage, setBranding } = useBrandingContext();
  const cardBg = theme.colors.primary[4];
  const primaryTextColor = theme.colors.primaryTextColor[6];
  const { locationId = "", orgId = "" } = useParams();
  const [searchedPromo, setSearchedPromo] = useState("");
  const [isPromoSearched, setIsPromoSearched] = useState(false);
  const [activeOfferings, setactiveOfferings] = useState<
    | LocationOfferingManyQuery["locationOfferingMany"]
    | LocationOfferingByPromoCodeQuery["locationOfferingByPromoCode"]
  >([]);

  useEffect(() => {
    setIsFlatPage(true);
    return () => {
      setIsFlatPage(false);
    };
  }, []);

  const { data: Offerings, loading } = useLocationOfferingManyQuery({
    variables: {
      locationId: locationId,
      isPromoCode: false,
    },
    skip: !locationId,
    onCompleted: () => {
      if (Offerings?.locationOfferingMany) {
        setactiveOfferings(
          Offerings?.locationOfferingMany.filter((e) => e.isActive) || []
        );
      }
    },
  });

  const [
    getPromoCodeOfferings,
    { data: promoCodeOfferings, loading: promoCodeLoading },
  ] = useLocationOfferingByPromoCodeLazyQuery({
    onCompleted: () => {
      setactiveOfferings(promoCodeOfferings?.locationOfferingByPromoCode || []);
    },
  });

  const { data: branding } = useGetOrganizationBrandingQuery({
    variables: {
      organizationId: orgId,
    },
    skip: !orgId,
    onCompleted: () => {
      setBranding(
        branding?.organizationBrandingOne
          ? branding.organizationBrandingOne
          : null
      );
    },
  });

  const handlePromoOfferings = () => {
    setIsPromoSearched(true);
    if (searchedPromo.length) {
      getPromoCodeOfferings({
        variables: {
          promoCode: searchedPromo,
        },
        fetchPolicy: "no-cache",
      });
    }
  };

  const handleBuy = (id: string) => {
    openConfirmModal({
      closeOnClickOutside: false,
      title: "SignUp",
      children: (
        <Text size="md" align="center" fw={500}>
          You need to create a new Account
        </Text>
      ),
      labels: { confirm: "Confirm", cancel: "Cancel" },
      onConfirm: () => {
        // open link in blank page
        window.open(
          `/${orgId}/${PATH_NAMES.LOGIN}?locationOfferingId=${id}&locationId=${locationId}`,
          "_blank"
        );
        closeAllModals();
      },
    });
  };

  useEffect(() => {
    if (!isPromoSearched) {
      setactiveOfferings(
        Offerings?.locationOfferingMany?.filter((e) => e.isActive) || []
      );
    } else {
      setactiveOfferings(promoCodeOfferings?.locationOfferingByPromoCode || []);
    }
  }, [isPromoSearched]);

  if (loading)
    return (
      <Loader size={"xl"} width={"100%"} mt={"xl"} sx={{ margin: "auto" }} />
    );
  if (!activeOfferings?.length)
    return (
      <ScrollArea sx={{ height: `calc(100vh - 108px)` }} scrollbarSize={6}>
        <Flex w={"100%"} justify={"center"} align={"flex-end"}>
          <TextInput
            w={"30%"}
            label="PromoCode"
            placeholder="Search offerings with promo code"
            value={searchedPromo}
            onChange={(e) => setSearchedPromo(e.target.value)}
          />
          <Button
            radius="md"
            size="sm"
            w={"fit-content"}
            mx={10}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
            onClick={() => handlePromoOfferings()}
            loading={promoCodeLoading}
            disabled={!searchedPromo.length}
          >
            Submit
          </Button>
        </Flex>
        <Flex direction={"column"} align={"center"}>
          <Text size="xl" align="center" fw={500} mt="xl">
            {isPromoSearched
              ? "No promotions found matching the provided code."
              : "No Location Offering Found"}
          </Text>
          {isPromoSearched ? (
            <Button
              radius="md"
              size="sm"
              w={"fit-content"}
              my={25}
              sx={{
                color: theme.colors.primaryTextColor[6],
                "&:hover": {
                  backgroundColor: theme.colors.primary[4],
                },
              }}
              onClick={() => {
                setSearchedPromo("");
                setIsPromoSearched(false);
                setactiveOfferings(
                  Offerings?.locationOfferingMany?.filter((e) => e.isActive) ||
                    []
                );
              }}
            >
              Back to Offerings
            </Button>
          ) : null}
        </Flex>
      </ScrollArea>
    );
  return (
    <>
      <Flex w={"100%"} justify={"center"} align={"flex-end"}>
        <TextInput
          w={"30%"}
          label="PromoCode"
          placeholder="Search offerings with promo code"
          value={searchedPromo}
          onChange={(e) => setSearchedPromo(e.target.value)}
        />
        <Button
          radius="md"
          size="sm"
          w={"fit-content"}
          mx={10}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
          onClick={() => handlePromoOfferings()}
          loading={promoCodeLoading}
          disabled={!searchedPromo.length}
        >
          Submit
        </Button>
      </Flex>
      <ScrollArea sx={{ height: `calc(100vh - 108px)` }} scrollbarSize={6}>
        <Flex
          wrap={"wrap"}
          gap={"lg"}
          m={"sm"}
          align={"stretch"}
          justify={"center"}
          sx={{ height: "100%" }}
        >
          {activeOfferings?.map((e) => (
            <Card
              sx={{ maxWidth: "22%", minWidth: 350 }}
              key={e.id}
              shadow="sm"
              p="lg"
              radius="md"
              withBorder
            >
              <Card.Section>
                <Text
                  size="xl"
                  align="center"
                  bg={cardBg}
                  p={"xs"}
                  color={primaryTextColor}
                >
                  {e.header}
                </Text>
              </Card.Section>

              <Text mt="md" weight={500}>
                {e.name}
              </Text>

              <Text mb="xs" size={14} c="dimmed">
                {e.subtext}
              </Text>

              <List
                spacing="xs"
                size="sm"
                center
                icon={
                  <ThemeIcon color="teal" size={20} radius="xl">
                    <AiFillCheckCircle size="1rem" />
                  </ThemeIcon>
                }
              >
                {e.details.map((detail) => (
                  <List.Item key={detail}> {detail}</List.Item>
                ))}
              </List>

              <Divider mt={"sm"} />
              <Flex justify={"space-between"} pt={"xs"}>
                <Text size="sm" fw={600}>
                  Agreement Name
                </Text>
                <Text size="sm">{e.agreement.name}</Text>
              </Flex>

              <Button
                size="sm"
                fullWidth
                mt="md"
                radius="md"
                onClick={() => handleBuy(e.id)}
              >
                Buy Now
              </Button>
            </Card>
          ))}
        </Flex>
        <Flex
          wrap={"wrap"}
          gap={"lg"}
          m={"sm"}
          align={"stretch"}
          justify={"center"}
          sx={{ height: "100%" }}
        >
          <Button
            radius="md"
            size="sm"
            w={"fit-content"}
            my={25}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
            onClick={() => {
              setSearchedPromo("");
              setIsPromoSearched(false);
              setactiveOfferings(
                Offerings?.locationOfferingMany?.filter((e) => e.isActive) || []
              );
            }}
          >
            Back to Offerings
          </Button>
        </Flex>
      </ScrollArea>
    </>
  );
}

export default MemberShips;
